import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import { t } from "@/lib/i18n";

const Field = ({ label, ...props }) => (
  <label className="block"><span className="ff-label">{t(label)}</span><input className="ff-field" {...props} /></label>
);

export const AuthDialog = ({ open, onClose, onDone }) => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isRegister = mode === "register";
  const isForgot = mode === "forgot";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (isForgot) {
      try {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Se l'email è registrata, riceverai a breve il link per reimpostare la password");
        setMode("login");
      } catch (err) {
        setError(apiError(err, "Invio non riuscito"));
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const { data } = await api.post(isRegister ? "/auth/register" : "/auth/login", payload);
      setUser(data);
      toast.success(isRegister ? "Account creato • puoi già pubblicare e prenotare corse" : `Bentornato ${data.name}`);
      onDone(isRegister);
    } catch (err) {
      setError(apiError(err, "Accesso non riuscito"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="auth-dialog" className="ff-glass-panel sm:max-w-md rounded-2xl p-5 sm:p-7">
        <DialogHeader className="text-left">
          <DialogTitle className="ff-display text-2xl font-extrabold">{isForgot ? t("Recupera password") : isRegister ? t("Crea account autista") : t("Accedi a FLEETFLOW")}</DialogTitle>
          <DialogDescription className="text-[#CBD5E1] text-sm">{t("Profilo, licenza e corse salvati su ogni dispositivo.")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="auth-tab-login" data-active={!isRegister} onClick={() => setMode("login")} className="ff-chip !rounded-xl h-12">{t("Accedi")}</button>
          <button type="button" data-testid="auth-tab-register" data-active={isRegister} onClick={() => setMode("register")} className="ff-chip !rounded-xl h-12">{t("Registrati")}</button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="auth-form">
          {isRegister && <Field label="Nome e Cognome / Ragione sociale" required minLength={2} data-testid="auth-name-input" value={form.name} onChange={set("name")} />}
          <Field label="Email" type="email" required autoComplete="email" data-testid="auth-email-input" value={form.email} onChange={set("email")} />
          {!isForgot && <Field label="Password (min. 8 caratteri)" type="password" required minLength={8} autoComplete={isRegister ? "new-password" : "current-password"} data-testid="auth-password-input" value={form.password} onChange={set("password")} />}
          {error && <p data-testid="auth-error" className="text-sm font-semibold text-red-400">{error}</p>}
          <button type="submit" disabled={loading} data-testid="auth-submit-button" className="ff-btn-primary w-full uppercase text-base">
            {loading ? t("Attendere…") : isForgot ? t("INVIA LINK DI RECUPERO") : isRegister ? t("CREA ACCOUNT") : t("ACCEDI")}
          </button>
        </form>
        {!isRegister && (
          <button type="button" data-testid="auth-forgot-toggle" onClick={() => setMode(isForgot ? "login" : "forgot")} className="text-sm font-semibold text-[#67E8F9] hover:text-white text-left min-h-[44px]">
            {isForgot ? t("Torna all'accesso") : t("Password dimenticata?")}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
